/**
 * Reviews the user wrote, with hide/unhide (Admin or Moderator) and delete (Admin). A review is either public or a
 * private note to self; both are listed, and the private ones are marked so an admin does not mistake one for
 * public content. A moderator-hidden review carries a Hidden chip with the reason.
 */

import { useState } from 'react'
import { Box, Chip, FormControl, InputLabel, MenuItem, Select, Typography } from '@mui/material'
import { Delete, RateReview, Visibility, VisibilityOff } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import { DataTable, type DataTableColumn } from '@components/common/DataTable'
import { userDataApi } from '@api/endpoints/userData'
import type { AdminUserReviewDto } from '@api/types/userData'
import { UserDataActionDialog } from './UserDataActionDialog'
import { UserDataChangeResultDialog } from './UserDataChangeResultDialog'
import { UserDataSection } from './UserDataSection'
import { adminUserReviewsKey } from './userQueryKeys'
import { errorMessage, formatDay } from './userFormatting'
import { useTablePaging } from './useTablePaging'
import { useUserDataChange } from './useUserDataChange'
import { HiddenReviewChip, ReviewHideDialog } from './ReviewHideControls'
import { useIsAdmin } from './useIsAdmin'

type VisibilityFilter = 'all' | 'visible' | 'hidden'

const targetLabel = (r: AdminUserReviewDto) => r.targetTitle || `${r.targetType} ${r.targetId.slice(0, 8)}`

export function UserReviewsSection({ userId }: { userId: string }) {
  const change = useUserDataChange(userId)
  const isAdmin = useIsAdmin()
  const [target, setTarget] = useState<AdminUserReviewDto | null>(null)
  const [action, setAction] = useState<'delete' | 'hide'>('delete')
  const [visibility, setVisibility] = useState<VisibilityFilter>('all')
  const paging = useTablePaging()

  const { data, isLoading, error } = useQuery({
    queryKey: adminUserReviewsKey(userId),
    queryFn: () => userDataApi.getReviews(userId),
  })

  const rows = (data ?? []).filter((r) =>
    visibility === 'all' ? true : visibility === 'hidden' ? r.isHidden : !r.isHidden,
  )

  const open = (r: AdminUserReviewDto, kind: 'delete' | 'hide') => {
    setAction(kind)
    setTarget(r)
  }

  const close = () => {
    setTarget(null)
    change.resetError()
  }

  const columns: DataTableColumn<AdminUserReviewDto>[] = [
    { key: 'target', header: 'Reviewed', primary: true, render: targetLabel },
    {
      key: 'type',
      header: 'Type',
      render: (r) => (
        <Box sx={{ display: 'inline-flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Chip size="small" variant="outlined" label={r.targetType} />
          {r.isPrivateNote && <Chip size="small" color="secondary" variant="outlined" label="Private note" />}
          <HiddenReviewChip review={r} />
        </Box>
      ),
    },
    { key: 'score', header: 'Score', align: 'right', render: (r) => (r.averageScore != null ? r.averageScore.toFixed(1) : '—') },
    {
      key: 'comment',
      header: 'Comment',
      render: (r) =>
        r.comment ? (
          <Typography
            variant="body2"
            component="span"
            title={r.comment}
            sx={{ display: 'block', maxWidth: { xs: 200, sm: 320 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {r.comment}
          </Typography>
        ) : (
          '—'
        ),
    },
    { key: 'created', header: 'Written', hideBelow: 'md', render: (r) => formatDay(r.createdAt) },
  ]

  return (
    <UserDataSection title="Reviews" icon={<RateReview fontSize="small" />}>
      <FormControl size="small" sx={{ mb: 1.5, width: { xs: '100%', sm: 160 } }}>
        <InputLabel>Show</InputLabel>
        <Select
          label="Show"
          value={visibility}
          onChange={(e) => {
            setVisibility(e.target.value as VisibilityFilter)
            paging.onPageChange(0)
          }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="visible">Visible</MenuItem>
          <MenuItem value="hidden">Hidden</MenuItem>
        </Select>
      </FormControl>
      <DataTable
        rows={rows}
        columns={columns}
        getRowKey={(r) => r.id}
        loading={isLoading}
        error={error ? errorMessage(error, 'Failed to load reviews') : null}
        emptyMessage="No reviews"
        dense
        {...paging}
        getRowActions={(r) => [
          {
            key: 'hide',
            label: r.isHidden ? 'Unhide review' : 'Hide review',
            icon: r.isHidden ? <Visibility fontSize="small" /> : <VisibilityOff fontSize="small" />,
            onClick: () => open(r, 'hide'),
          },
          ...(isAdmin
            ? [{ key: 'delete', label: 'Delete review', icon: <Delete fontSize="small" />, color: 'error' as const, onClick: () => open(r, 'delete') }]
            : []),
        ]}
      />

      {target && action === 'hide' && (
        <ReviewHideDialog
          review={target}
          description={`${target.isPrivateNote ? 'Private note' : 'Review'} of ${targetLabel(target)}, written ${formatDay(target.createdAt)}`}
          pending={change.pending}
          error={change.error}
          submit={change.submit}
          onDone={close}
          onClose={close}
        />
      )}

      {target && action === 'delete' && (
        <UserDataActionDialog
          title="Delete review?"
          message={`${target.isPrivateNote ? 'Private note' : 'Review'} of ${targetLabel(target)}, written ${formatDay(target.createdAt)}. This cannot be undone.`}
          confirmLabel="Delete review"
          destructive
          pending={change.pending}
          error={change.error}
          onSubmit={(reason) => change.submit(() => userDataApi.deleteReview(userId, target.id, reason), close)}
          onClose={close}
        />
      )}

      <UserDataChangeResultDialog open={change.resultOpen} result={change.result} onClose={change.closeResult} />
    </UserDataSection>
  )
}
